import { SelectItem, Link } from '@panwds/react-ui';
import { useForm } from 'react-hook-form';
import { useTranslate } from "../../customHooks";
import { isEmpty } from 'lodash';
import { toast } from '../../components';
import { useCallback, useEffect, useState } from 'react';
import { useLazyGetSubscriptionsQuery } from '../../redux/services/subscriptions-service';
import AppConfig from '../../config.json';
import { oktaProvider } from '../../authProvider';
import jwt_decode from "jwt-decode";
import { useAssociateBillingAccessCodeMutation } from '../../redux/services/scm-billing-association-service';
import { AssociateBillingAccessCodeResponse, CreateLinkAccountRequest } from '../../interfaces/interfaces';
import { useCreateLinkAccountMutation } from '../../redux/services/accounts-service';
import { useCreateTenantMutation } from '../../redux/services/tenant-service';
import CircularLoader from '../../components/CircularLoader/CircularLoader';
import { SUBSCRIPTION_ACCOUNT_STATUS } from '../../utils/constants';
import { RouteUri } from '../../routeUri';
import { useHistory } from 'react-router-dom';
import { isJwtExpired, getEmailFromToken } from '../../utils/authUtils';
import { useValidateBillingCode } from './hooks/useValidateBillingCode';
import { SubscriptionButton } from './components/ButtonComponents';
import { CloudNgfwLinkAccountForm } from './components/CloudNgfwLinkAccountForm';
import { AirfmLinkAccountForm } from './components/AirfmLinkAccountForm';
import { Body } from '@panwds/react-ui';
import {
    SUBSCRIPTION_I18N_KEYS,
    SUBSCRIPTION_METRICS,
    SUBSCRIPTION_TOAST_IDS
} from './constants';

export const SubscriptionLinkAccount = (props: any) => {
    const translate = useTranslate();
    const history = useHistory();
    const { tokens, queryString } = props?.state;
    // Product listing type is passed via listing query parameter
    // After Okta redirect, params are in queryString from state, not URL
    const listing = queryString?.listing === 'AIRFM' || queryString?.listing === 'CNGFW'
        ? queryString.listing
        : 'CNGFW';
    const isAirfm = listing === 'AIRFM';
    // Okta passes tokens as an object ({ idToken: { idToken: '...' } });
    // Cognito (via Login.tsx) passes the raw JWT string directly.
    const idToken = typeof tokens === 'string' ? tokens : tokens?.idToken?.idToken;
    // A missing or malformed token must not throw during render. isJwtExpired() treats it as
    // expired, so the expired-session screen (with a Login button) is shown instead of a crash.
    let decoded: any;
    try {
        decoded = jwt_decode(idToken);
    } catch (e) {
        decoded = undefined;
    }
    const email = getEmailFromToken(idToken) || decoded?.email;

    const [tenantChoices, setTenantChoices] = useState<
        SelectItem[] | undefined
    >([]);
    const issuerIdpO = tokens?.idToken?.issuer === AppConfig?.oidc?.issuer
    const BILLING_ASSOCIATION_CODE = queryString['BillingAssociationCode'];
    const ACCOUNT_STATUS = queryString["link-state"];
    const isAccountAlreadyLinked = ACCOUNT_STATUS === SUBSCRIPTION_ACCOUNT_STATUS.LINKED ||
        ACCOUNT_STATUS === SUBSCRIPTION_ACCOUNT_STATUS.IN_METERING_GROUP;
    const FIRST_NAME = queryString['FirstName'] || "";
    const LAST_NAME = queryString['LastName'] || "";
    const [nextStep, setNextStep] = useState<{ TsgId: string } | { AwsAccountId: string } | undefined>()

    const [triggerSubscriptions, { data: subscriptions, error, isFetching: subscriptionsLoading }] = useLazyGetSubscriptionsQuery();
    const [triggerAssociateBillingAccessCode, { error: billingAssociationFailed, isLoading: associatingBillingCode }] = useAssociateBillingAccessCodeMutation();
    const [triggerCreateLinkAccount, { error: linkAccountFailed, isLoading: creatingLinkAccount }] = useCreateLinkAccountMutation();
    const [createTenant, { error: createTenantFailed, isLoading: creatingTenant }] = useCreateTenantMutation();
    const { validateCode, isValidating: validatingBillingCode } = useValidateBillingCode();

    const formMethods = useForm({});
    const {
        formState: { isValid }
    } = formMethods

    useEffect(() => {
        if (queryString && !isJwtExpired(idToken)) {
            triggerSubscriptions({
                payload: {
                    LoginType: issuerIdpO ? "IdpO" : "IdpC",
                    AccountId: queryString['acc-id'],
                    ProductCode: queryString['product-code'],
                    // TODO: Add product-specific payload fields when AIRFM API is defined
                },
                headers: {
                    Authorization: idToken,
                    'Content-Type': 'application/json',
                },
            });
        }
    }, [tokens, issuerIdpO, queryString, isAirfm]);

    useEffect(() => {
        if (!isEmpty(subscriptions)) {
            // Auto-submit when there's a single tenant
            // AIRFM: Only auto-submit when BAC is provided (otherwise need CSP/TSG selection)
            // CNGFW: Always auto-submit with single tenant
            const shouldAutoSubmit = subscriptions?.length === 1 && (!isAirfm || BILLING_ASSOCIATION_CODE);

            if (shouldAutoSubmit) {
                //Mimic the handleSubmit of the form when there are more than 1 tenants
                if (isAirfm) {
                    handleAirfmLinkAccount({
                        TenantId: subscriptions[0]?.SubscriptionKey,
                        BillingAssociationCode: BILLING_ASSOCIATION_CODE
                    });
                } else {
                    handleLinkAccountCreate({
                        TenantId: subscriptions[0]?.SubscriptionKey,
                        BillingAssociationCode: BILLING_ASSOCIATION_CODE
                    });
                }
            }
            setTenantChoices(
                subscriptions?.map((data: any) => ({
                    children: (data?.CspSerialNumber) ? `${data?.LinkAccountId} (CSP: ${data?.CspSerialNumber})` : data?.LinkAccountId,
                    value: data?.SubscriptionKey,
                    context: {
                        TenantVersion: data?.TenantVersion
                    },
                }))
            );
        }
        else {
            setTenantChoices(undefined);
        }
    }, [subscriptions, BILLING_ASSOCIATION_CODE, isAirfm]);

    useEffect(() => {
        if (error) {
            toast.error((error as any)?.error, { toastId: SUBSCRIPTION_TOAST_IDS.LIST_SUBSCRIPTIONS });
            setTenantChoices(undefined);
        }
    }, [error]);

    useEffect(() => {
        if (linkAccountFailed) {
            toast.error((linkAccountFailed as any)?.error || translate(SUBSCRIPTION_I18N_KEYS.REQUEST_FAILED), {
                toastId: SUBSCRIPTION_TOAST_IDS.CREATE_SUBSCRIPTION_LINK_ACCOUNT
            });
        }
    }, [linkAccountFailed]);

    useEffect(() => {
        if (billingAssociationFailed) {
            toast.error((billingAssociationFailed as any)?.error || translate(SUBSCRIPTION_I18N_KEYS.REQUEST_FAILED), {
                toastId: SUBSCRIPTION_TOAST_IDS.ASSOCIATE_SCM_CODE
            });
        }
    }, [billingAssociationFailed]);


    /**
 * Associates a billing access code with the user's account
 * @param email User email address
 * @param awsAccountId AWS Account ID
 * @param tenant Tenant ID
 * @param listing Optional listing type (CNGFW or AIRFM)
 * @returns Result of the association operation
 */
    const associateBillingAccessCode = useCallback(async (email: string, awsAccountId: string, tenant: string, listing?: 'CNGFW' | 'AIRFM', billingAccessCode?: string) => {
        const code = billingAccessCode || BILLING_ASSOCIATION_CODE;

        // Validate BAC before associating only on retry (when form provides the code)
        if (billingAccessCode) {
            const isValid = await validateCode(email, code, awsAccountId, listing);
            if (!isValid) return;
        }

        try {
            const billingCodeAssociated: AssociateBillingAccessCodeResponse = await triggerAssociateBillingAccessCode({
                payload: {
                    UserEmail: email,
                    BillingAccessCode: code,
                    AWSAccountID: awsAccountId,
                    ...(listing && { Listing: listing }),
                },
                headers: {
                    'Authorization': idToken,
                    'x-api-key': tenant,
                },
            }).unwrap();

            if (billingCodeAssociated?.TsgID) {
                setNextStep({ TsgId: billingCodeAssociated?.TsgID });
            }
        } catch (err: any) {
            // Error toast handled by useEffect on billingAssociationFailed
        }

    }, [SUBSCRIPTION_TOAST_IDS.ASSOCIATE_SCM_CODE, validateCode]);

    /**
     * Handler for AIRFM account linking
     * - With BAC: Creates link account and associates billing code (no tenant creation)
     * - Without BAC: Creates link account, then tenant with CSP/TSG details
     */
    const handleAirfmLinkAccount = async (params: any = {}) => {
        const { TenantId, CspId, TsgId, TsgIdObject: tsgObject, BillingAssociationCode: formBac } = params;
        const AccoutId = queryString["acc-id"] || "";
        const marketplaceToken = queryString["marketplace-token"] || "";
        const tenant = TenantId || tenantChoices?.[0]?.value || "";

        // Prepare headers for API call
        const headers = {
            Authorization: idToken,
            'x-api-key': String(tenant),
            'Content-Type': 'application/json',
        };

        // BAC workflow - similar to CNGFW
        if (BILLING_ASSOCIATION_CODE) {
            try {
                const createAccountPayload: CreateLinkAccountRequest = {
                    Origin: "AwsMarketplace",
                    AWSMarketplaceToken: marketplaceToken,
                    AccountId: AccoutId
                };
                if (isAccountAlreadyLinked) {
                    // Link-account is idempotent for an already-linked account; call it on every landing
                    // so the metering group is kept current. A failure must not block the BAC association.
                    await triggerCreateLinkAccount({ payload: createAccountPayload, headers: headers }).unwrap().catch(() => undefined);
                    associateBillingAccessCode(email, AccoutId, String(tenant), listing, formBac);
                } else {
                    // Create link account first
                    const accountCreated = await triggerCreateLinkAccount({
                        payload: createAccountPayload,
                        headers: headers,
                    }).unwrap();

                    if (accountCreated?.AccountId) {
                        // Associate billing code after link account creation
                        associateBillingAccessCode(email, accountCreated.AccountId, String(tenant), listing, formBac);
                    }
                }
                // Set next step with account ID for redirect
                setNextStep({ AwsAccountId: AccoutId });
            } catch (err) {
                // Error already handled by associateBillingAccessCode
            }
            return;
        }

        // Non-BAC workflow - requires CSP/TSG selection and tenant creation
        // Extract values from the TSG integration object (stored in context by form)
        // Object structure: { CspSupportAccountId, TsgId, TenantName, Region, Url, CDLId, CDLRegion, ... }
        const {
            TenantName = "",
            Url = "",
            Region = "",
            CDLId = "",
            CDLRegion = ""
        } = tsgObject || {};

        try {
            // Step 1: Create link account first (without ProductId and AgreementId for AIRFM)
            const createAccountPayload: CreateLinkAccountRequest = {
                Origin: "AwsMarketplace",
                AWSMarketplaceToken: marketplaceToken,
                AccountId: AccoutId
            };

            const accountCreated = await triggerCreateLinkAccount({
                payload: createAccountPayload,
                headers: headers,
            }).unwrap();

            // Check if link account creation was successful
            if (!accountCreated || !accountCreated.AccountId) {
                throw new Error('Link account creation failed - no account ID returned');
            }

            // Step 2: Create tenant ONLY if link account was created successfully
            try {
                const result = await createTenant({
                    payload: {
                        CspSupportAccountId: CspId,
                        CspAccountName: "",
                        UserEmail: email,
                        FirstName: FIRST_NAME,
                        LastName: LAST_NAME,
                        TenantName: TenantName,
                        TsgId: TsgId,
                        Url: Url,
                        SCMRegion: Region, // Map from tsgObject.Region
                        ...(CDLId && { CDLId }), // Include if available
                        ...(CDLRegion && { CDLRegion }), // Include if available
                        AwsMarketplaceToken: marketplaceToken,
                    },
                    headers: headers,
                }).unwrap();

                // Success - proceed to next step with TsgId
                setNextStep({ TsgId: TsgId });
            } catch (tenantError: any) {
                toast.error(`Link account created but tenant creation failed: ${tenantError?.error || tenantError?.message || 'Unknown error'}`, {
                    toastId: SUBSCRIPTION_TOAST_IDS.CREATE_TENANT
                });
            }
        } catch (error: any) {
            // Error toast handled by useEffect on linkAccountFailed
        }
    };

    const handleLinkAccountCreate = async (params: any = {}) => {
        const { TenantId, BillingAssociationCode } = params;
        const AWSMarketplaceToken = queryString["marketplace-token"] || "";
        const ProductId = queryString["product-id"] || "";
        const AgreementId = queryString["agreement-id"] || "";
        const AccoutId = queryString["acc-id"] || ""
        const Tenant = TenantId || tenantChoices?.[0]?.value;

        const createAccountPayload: CreateLinkAccountRequest = {
            Origin: "AwsMarketplace",
            AWSMarketplaceToken: AWSMarketplaceToken,
            ProductId: ProductId,
            AgreementId: AgreementId,
            AccountId: AccoutId
        };

        if (BillingAssociationCode) {

            try {
                const linkHeaders = {
                    Authorization: idToken,
                    'x-api-key': Tenant,
                    'Content-Type': 'application/json',
                };
                if (isAccountAlreadyLinked) {
                    // Link-account is idempotent for an already-linked account; call it on every landing
                    // so the metering group is kept current. A failure must not block the BAC association.
                    await triggerCreateLinkAccount({ payload: createAccountPayload, headers: linkHeaders }).unwrap().catch(() => undefined);
                    associateBillingAccessCode(email, AccoutId, Tenant, listing, BillingAssociationCode);
                } else {
                    const accountCreated = await triggerCreateLinkAccount({
                        payload: createAccountPayload,
                        headers: linkHeaders,
                    }).unwrap();
                    if (accountCreated?.AccountId) {
                        associateBillingAccessCode(email, accountCreated?.AccountId, Tenant, listing, BillingAssociationCode);
                    }
                }
            } catch (err: any) {
                // Error toast handled by useEffect on linkAccountFailed/billingAssociationFailed
            }
        } else {
            try {
                const accountCreated = await triggerCreateLinkAccount({
                    payload: createAccountPayload,
                    headers: {
                        Authorization: idToken,
                        'x-api-key': Tenant,
                        'Content-Type': 'application/json',
                    },
                }).unwrap();
                if (accountCreated?.AccountId) {
                    // When microsubscriptions flag is enabled, store session and redirect to dashboard
                    if (process.env.REACT_APP_MICROSUBSCRIPTIONS === 'true') {
                        const tenantVersion = tenantChoices?.find(t => t.value === Tenant)?.context?.TenantVersion || '';
                        localStorage.setItem('idToken', idToken);
                        sessionStorage.setItem('tenant', String(Tenant));
                        sessionStorage.setItem('tenantVersion', tenantVersion);
                        history.replace(RouteUri.Dashboard);
                    } else {
                        setNextStep({ AwsAccountId: accountCreated?.AccountId });
                    }
                }
            } catch (err: any) {
                // Error toast handled by useEffect on linkAccountFailed
            }
        }
    };

    return (
        <>
            {nextStep && 'TsgId' in nextStep ?
                    (
                        <Body as="div" addClassName="tw-px-4 tw-py-2 tw-space-y-4">
                            <Body size="lg" addClassName="tw-font-semibold" style={{ fontSize: '20px' }}>
                                {translate(SUBSCRIPTION_I18N_KEYS.SCM_BILLING_SUBTITLE)}
                            </Body>
                            <Body size="md" style={{ fontSize: '16px' }}>{translate(SUBSCRIPTION_I18N_KEYS.SCM_BILLING_DESCRIPTION)}</Body>
                            <SubscriptionButton
                                dataMetrics={SUBSCRIPTION_METRICS.SIGN_UP_SCM_CODE_ASSOCIATED}
                                type="submit"
                            >
                                <Link
                                    target="_blank"
                                    href={`${isAirfm ? import.meta.env.VITE_AIRFM_SCM_URL : import.meta.env.VITE_SCM_URL}?${nextStep?.TsgId}`}
                                    external
                                >
                                    {translate(SUBSCRIPTION_I18N_KEYS.SCM_BILLING_SUBMIT)}
                                </Link>
                            </SubscriptionButton>
                        </Body>
                    ) :
                    nextStep && 'AwsAccountId' in nextStep ?
                        (
                            <Body as="div" addClassName="tw-px-4 tw-py-2 tw-space-y-4">
                                <Body size="lg" addClassName="tw-font-semibold" style={{ fontSize: '20px' }}>
                                    {translate(SUBSCRIPTION_I18N_KEYS.LINK_TENANT_SUBTITLE)}
                                </Body>
                                <Body size="md" style={{ fontSize: '16px' }}>{translate(SUBSCRIPTION_I18N_KEYS.LINK_TENANT_DESCRIPTION)}</Body>
                                <SubscriptionButton
                                    dataMetrics={SUBSCRIPTION_METRICS.LINK_TENANT_LOGIN}
                                    type="submit"
                                    onClick={() => {
                                        history.replace(RouteUri.Login);
                                    }}
                                >
                                    {translate(SUBSCRIPTION_I18N_KEYS.LOGIN)}
                                </SubscriptionButton>
                            </Body>
                        ) : (subscriptionsLoading || creatingLinkAccount || associatingBillingCode || creatingTenant || validatingBillingCode) ?
                            (
                                <Body as="div" addClassName="tw-px-4 tw-py-2 tw-relative" style={{ minHeight: '200px' }}>
                                    <CircularLoader loading={true} />
                                </Body>
                            ) :
                                (
                                    (isJwtExpired(idToken)) ? (
                                        <Body as="div" addClassName="tw-px-4 tw-py-2 tw-space-y-4">
                                            <Body size="lg" addClassName="tw-font-semibold" style={{ fontSize: '20px' }}>
                                                {translate(SUBSCRIPTION_I18N_KEYS.NO_TENANT_EXPIRED_SESSION)}
                                            </Body>
                                            <SubscriptionButton
                                                dataMetrics={SUBSCRIPTION_METRICS.EXPIRED_SESSION_LOGIN}
                                                onClick={() => {
                                                    const options = {
                                                        state: queryString
                                                    };
                                                    oktaProvider.signIn(options);
                                                }}
                                            >
                                                {translate(SUBSCRIPTION_I18N_KEYS.LOGIN)}
                                            </SubscriptionButton>
                                        </Body>
                                    )
                                        : (tenantChoices === undefined && !subscriptionsLoading) ? (
                                            <Body as="div" addClassName="tw-px-4 tw-py-2 tw-space-y-4">
                                                <Body size="lg" addClassName="tw-font-semibold" style={{ fontSize: '20px' }}>
                                                    {translate(SUBSCRIPTION_I18N_KEYS.NO_TENANT_SUBTITLE)}
                                                </Body>
                                                <Body size="md" style={{ fontSize: '16px' }}>{translate(SUBSCRIPTION_I18N_KEYS.NO_TENANT_DESCRIPTION)}</Body>
                                            </Body>
                                        ) : (tenantChoices && (
                                            // AIRFM: show form for multi-tenant OR no BAC OR on error (retry)
                                            // CNGFW: show form for multi-tenant OR on error (retry with single tenant)
                                            isAirfm
                                                ? (tenantChoices.length > 1 || !BILLING_ASSOCIATION_CODE || linkAccountFailed || billingAssociationFailed)
                                                : (tenantChoices.length > 1 || linkAccountFailed || billingAssociationFailed)
                                        )) && (
                                            <>
                                                {isAirfm ? (
                                                    <AirfmLinkAccountForm
                                                        formMethods={formMethods}
                                                        accountId={queryString['acc-id']}
                                                        email={email}
                                                        tenantChoices={tenantChoices}
                                                        subscriptionsLoading={subscriptionsLoading}
                                                        billingAssociationCode={BILLING_ASSOCIATION_CODE}
                                                        isValid={isValid}
                                                        hasError={!!(linkAccountFailed || billingAssociationFailed)}
                                                        onSubmit={handleAirfmLinkAccount}
                                                        authToken={idToken}
                                                    />
                                                ) : (
                                                    <CloudNgfwLinkAccountForm
                                                        formMethods={formMethods}
                                                        accountId={queryString['acc-id']}
                                                        email={email}
                                                        tenantChoices={tenantChoices}
                                                        subscriptionsLoading={subscriptionsLoading}
                                                        billingAssociationCode={BILLING_ASSOCIATION_CODE}
                                                        isValid={isValid}
                                                        hasError={!!(linkAccountFailed || billingAssociationFailed)}
                                                        onSubmit={handleLinkAccountCreate}
                                                    />
                                                )}
                                            </>
                                        )
                                )}
        </>
    );
};
